import { useState } from 'react';
import { CalendarDays, Clock3, Edit3, Plus, Trash2 } from 'lucide-react';
import type { ThemeId } from '../config/themes';
import { InstallGuide } from '../components/layout/PwaNotices';
import { ThemePicker } from '../components/layout/ThemePicker';
import { Pagination } from '../components/modules/Pagination';
import type { Category, Settings } from '../types';

type Props = {
  activeTheme: ThemeId;
  categories: readonly Category[];
  onAddCategory: () => void;
  onDeleteCategory: (category: Category) => void;
  onEditCategory: (category: Category) => void;
  onSettingsChange: (settings: Settings) => void;
  onSelectTheme: (themeId: ThemeId) => void;
  settings: Settings;
};

export const SettingsView = ({ activeTheme, categories, onAddCategory, onDeleteCategory, onEditCategory, onSelectTheme, onSettingsChange, settings }: Props) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.max(1, Math.ceil(categories.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedCategories = categories.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage);

  return (
    <div className="settings-layout">
      <div className="settings-options-column">
        <section className="settings-card settings-color-card" aria-labelledby="themes-title">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Three-color palette</p>
              <h2 id="themes-title">Planner color</h2>
            </div>
          </div>
          <ThemePicker activeTheme={activeTheme} onSelectTheme={onSelectTheme} placement="settings" />
        </section>

        <section className="settings-card settings-preferences-card" aria-labelledby="preferences-title">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Planner behavior</p>
              <h2 id="preferences-title">Preferences</h2>
            </div>
          </div>
          <div className="preferences-grid">
            <fieldset className="preference-choice">
              <legend>Week starts on</legend>
              <div className="preference-options">
                <button type="button" aria-pressed={settings.weekStartsOn === 0} onClick={() => onSettingsChange({ ...settings, weekStartsOn: 0 })}>
                  <CalendarDays size={16} aria-hidden="true" />
                  <span>Sunday</span>
                </button>
                <button type="button" aria-pressed={settings.weekStartsOn === 1} onClick={() => onSettingsChange({ ...settings, weekStartsOn: 1 })}>
                  <CalendarDays size={16} aria-hidden="true" />
                  <span>Monday</span>
                </button>
              </div>
            </fieldset>
            <fieldset className="preference-choice">
              <legend>Time format</legend>
              <div className="preference-options">
                <button type="button" aria-label="12-hour time" aria-pressed={settings.timeFormat === 12} onClick={() => onSettingsChange({ ...settings, timeFormat: 12 })}>
                  <Clock3 size={16} aria-hidden="true" />
                  <span>12</span>
                </button>
                <button type="button" aria-label="24-hour time" aria-pressed={settings.timeFormat === 24} onClick={() => onSettingsChange({ ...settings, timeFormat: 24 })}>
                  <Clock3 size={16} aria-hidden="true" />
                  <span>24</span>
                </button>
              </div>
            </fieldset>
          </div>
        </section>

        <InstallGuide />
      </div>

      <section className="settings-card settings-categories-card" aria-labelledby="categories-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Across every module</p>
            <h2 id="categories-title">Categories</h2>
          </div>
          <button type="button" className="secondary-button" onClick={onAddCategory}>
            <Plus size={18} />New category
          </button>
        </div>
        <div className="category-list-wrapper">
          <div className="category-list">
            {paginatedCategories.map((category) => (
              <article className="category-row" key={category.id}>
                <span className="category-color" style={{ backgroundColor: category.color }} aria-hidden="true" />
                <div>
                  <strong>{category.name}</strong>
                </div>
                <button
                  type="button"
                  className="icon-button small-icon-button"
                  aria-label={`Edit ${category.name}`}
                  onClick={() => onEditCategory(category)}
                >
                  <Edit3 size={16} />
                </button>
                <button
                  type="button"
                  className="icon-button small-icon-button destructive-button"
                  aria-label={category.kind === 'default' ? `${category.name} cannot be deleted` : `Delete ${category.name}`}
                  disabled={category.kind === 'default'}
                  onClick={() => onDeleteCategory(category)}
                >
                  <Trash2 size={16} />
                </button>
              </article>
            ))}
          </div>
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </section>
    </div>
  );
};
